import * as XLSX from 'xlsx';
import { getBatchStatus, getUsageRate } from '../../activation-code/utils/batchStatus';
import { getStatusDisplay, getTargetDisplay } from '../../activation-code/utils/activationCodeUtils';
import {
  BatchRow,
  OverviewData,
  Paged,
  ReportFilters,
  ReportTab,
  ReportUser,
  SummaryOptions,
  TargetRow,
  UsedCodeRow,
  UserRow,
} from '../types';
import { DELETED_USER, formatDateTime, formatYmd } from './format';
import { formatPeriod } from './period';

// 'ma-da-dung': file của hộp thoại mã đã dùng, không phải một tab
export type ExportTab = ReportTab | 'ma-da-dung';

// Trần số dòng ghi vào một sheet: khớp trần export của API và nằm dưới giới hạn dòng của Excel
export const MAX_EXPORT_ROWS = 50_000;

export const TRUNCATED_MESSAGE = 'Chỉ xuất 50.000 dòng đầu, hãy thu hẹp bộ lọc';
export const REVOKED_NOTE = 'Chỉ tính mã bị thu hồi; thu hồi tài sản của người dùng không làm giảm số này';
export const INVENTORY_NOTE = 'Tình trạng hiện tại, không theo ngày/trường';

const TYPE_LABELS = { book: 'Sách', collection: 'Bộ sách' } as const;

const nameOf = (list: { id: string; name: string }[] | undefined, id: string) =>
  list?.find((x) => x.id === id)?.name ?? id;
const titleOf = (list: { id: string; title: string }[] | undefined, id: string) =>
  list?.find((x) => x.id === id)?.title ?? id;

function summaryParts(f: ReportFilters, o: SummaryOptions = {}): string[] {
  const parts: string[] = [];
  if (f.from && f.to) parts.push(`Từ ${formatYmd(f.from)} đến ${formatYmd(f.to)}`);
  else if (f.from) parts.push(`Từ ${formatYmd(f.from)}`);
  else if (f.to) parts.push(`Đến ${formatYmd(f.to)}`);
  if (f.school_id) parts.push(`Trường: ${nameOf(o.schools, f.school_id)}`);
  if (f.class_id) parts.push(`Lớp: ${nameOf(o.classes, f.class_id)}`);
  if (f.book_id) parts.push(`Sách: ${titleOf(o.books, f.book_id)}`);
  if (f.collection_id) parts.push(`Bộ sách: ${titleOf(o.collections, f.collection_id)}`);
  if (f.batch_id) parts.push(`Lô: ${nameOf(o.batches, f.batch_id)}`);
  // Không ghi q: API chỉ áp q cho by-user nên userWorkbook tự thêm
  return parts;
}

const joinSummary = (parts: string[]) => (parts.length ? parts.join('; ') : 'Tất cả');

export const filterSummary = (f: ReportFilters, options?: SummaryOptions) => joinSummary(summaryParts(f, options));

const reportTitle = (summary: string) => `Báo cáo kích hoạt — ${summary}`;

export function exportFileName(tab: ExportTab, f: ReportFilters): string {
  const range = f.from || f.to ? `${f.from || 'tat-ca'}_${f.to || 'tat-ca'}` : 'tat-ca';
  return `bao-cao-kich-hoat_${tab}_${range}.xlsx`;
}

// Xuất lấy toàn bộ kết quả nên bỏ phân trang của bảng
export function exportFilters(f: ReportFilters): ReportFilters {
  const { page, per_page, ...rest } = f;
  return rest;
}

// Gọi API danh sách ở chế độ xuất rồi dựng workbook; `truncated` để nút xuất cảnh báo thiếu dòng
export async function exportList<T>(
  fetchList: (f: ReportFilters, opts: { export: true }) => Promise<Paged<T>>,
  filters: ReportFilters,
  build: (items: T[]) => XLSX.WorkBook
): Promise<{ workbook: XLSX.WorkBook; truncated?: boolean }> {
  const res = await fetchList(exportFilters(filters), { export: true });
  return { workbook: build(res.items), truncated: res.truncated };
}

// Dòng 1 là tiêu đề gộp ngang toàn bảng, dòng 2 là tiêu đề cột
function buildSheet(title: string, header: string[], widths: number[], rows: unknown[][], footer: unknown[][] = []) {
  const ws = XLSX.utils.aoa_to_sheet([[title], header, ...rows, ...footer]);
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } }];
  ws['!cols'] = widths.map((wch) => ({ wch }));
  return ws;
}

function workbookOf(sheets: [string, XLSX.WorkSheet][]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name));
  return wb;
}

// Bộ sách ghi rõ để không lẫn với sách lẻ cùng tên (trên bảng là nhãn "Bộ sách")
const targetLabel = (item: { book_title?: string; collection_title?: string }) => {
  const { isCollection, title } = getTargetDisplay(item);
  return isCollection ? `Bộ sách: ${title}` : title;
};

const userName = (user: ReportUser | null) => user?.name || DELETED_USER;
const joinNames = (list: { name: string }[]) => list.map((x) => x.name).join(', ');
const statusLabel = (status: string) => getStatusDisplay(status).label;

export function overviewWorkbook(data: OverviewData, f: ReportFilters, options?: SummaryOptions): XLSX.WorkBook {
  const title = reportTitle(filterSummary(f, options));
  const groupBy = f.group_by ?? 'day';
  const { kpis, inventory } = data;

  const kpi = buildSheet(
    title,
    ['Chỉ số', 'Giá trị'],
    [30, 45],
    [
      ['Lượt kích hoạt', kpis.activations],
      ['Người đã kích hoạt', kpis.activated_users],
      ['Sách đã kích hoạt', kpis.activated_books],
      ['Bộ sách đã kích hoạt', kpis.activated_collections],
    ],
    [
      [],
      ['Kho mã', INVENTORY_NOTE],
      ['Tổng mã', inventory.total],
      ['Đã dùng', inventory.used],
      ['Còn lại', inventory.unused],
      ['Đã thu hồi', inventory.revoked],
      ['Hết hạn', inventory.expired],
    ]
  );

  // Ngày ghi đủ năm vì file có thể trải nhiều năm; tuần/tháng dùng nhãn như biểu đồ
  const periodLabel = (p: string) => (groupBy === 'day' ? formatYmd(p) : formatPeriod(p, groupBy));
  const series = buildSheet(
    title,
    ['Kỳ', 'Lượt kích hoạt'],
    [16, 16],
    data.series.map((s) => [periodLabel(s.period), s.count])
  );

  return workbookOf([
    ['KPI', kpi],
    ['Theo thời gian', series],
  ]);
}

export function targetWorkbook(rows: TargetRow[], f: ReportFilters, options?: SummaryOptions): XLSX.WorkBook {
  const parts = summaryParts(f, options);
  if (f.type === 'book' || f.type === 'collection') parts.push(`Loại: ${TYPE_LABELS[f.type]}`);
  const ws = buildSheet(
    reportTitle(joinSummary(parts)),
    ['#', 'Tên', 'Lượt kích hoạt', 'Người dùng', 'Đã thu hồi', 'Sách trong bộ'],
    [6, 45, 16, 14, 14, 50],
    rows.map((r, i) => [
      i + 1,
      targetLabel(r),
      r.activations,
      r.users,
      r.revoked,
      r.target_type === 'collection' ? (r.books ?? []).map((b) => b.book_title).join(', ') : '',
    ]),
    [[], [REVOKED_NOTE]]
  );
  return workbookOf([['Theo sách', ws]]);
}

export function batchWorkbook(
  rows: BatchRow[],
  f: ReportFilters,
  options?: SummaryOptions,
  now: Date = new Date()
): XLSX.WorkBook {
  const ws = buildSheet(
    reportTitle(filterSummary(f, options)),
    [
      'Lô',
      'Đích',
      'Tổng',
      'Đã dùng',
      'Còn lại',
      'Đã thu hồi',
      'Hết hạn',
      'Tỉ lệ dùng',
      'Trạng thái',
      'Dùng trong kỳ',
      'Hạn dùng',
    ],
    [28, 40, 10, 10, 10, 12, 10, 12, 16, 14, 12],
    rows.map((r) => [
      r.name,
      targetLabel(r),
      r.total_codes,
      r.used_codes,
      r.unused_codes,
      r.revoked_codes,
      r.expired_codes,
      `${getUsageRate(r)}%`,
      getBatchStatus(r, now).label,
      r.used_in_period,
      formatYmd(r.expires_at, ''),
    ])
  );
  return workbookOf([['Theo lô mã', ws]]);
}

export function usedCodesWorkbook(rows: UsedCodeRow[], f: ReportFilters, options?: SummaryOptions): XLSX.WorkBook {
  const ws = buildSheet(
    reportTitle(filterSummary(f, options)),
    ['Mã', 'Người dùng', 'SĐT', 'Email', 'Trường', 'Ngày dùng', 'Trạng thái'],
    [20, 28, 14, 28, 30, 18, 14],
    rows.map((r) => [
      r.code,
      userName(r.user),
      r.user?.phone ?? '',
      r.user?.email ?? '',
      r.school_name ?? '',
      formatDateTime(r.used_at),
      statusLabel(r.status),
    ])
  );
  return workbookOf([['Mã đã dùng', ws]]);
}

// Mỗi mã một dòng, lặp thông tin người dùng để lọc/đếm được trong Excel
export function userWorkbook(rows: UserRow[], f: ReportFilters, options?: SummaryOptions): XLSX.WorkBook {
  return userWorkbookFromRows(userExportRows(rows).data, f, options);
}

// Bung mỗi người thành nhiều dòng (mỗi mã một dòng) nên số dòng có thể vượt trần export của API:
// trần đó tính theo số người, còn file ghi ra theo số mã. Dừng ở MAX_EXPORT_ROWS để không vượt
// giới hạn dòng của Excel và trả cờ bị cắt để nơi gọi cảnh báo.
export function userExportRows(
  rows: UserRow[],
  maxRows: number = MAX_EXPORT_ROWS
): { data: unknown[][]; truncated: boolean } {
  const data: unknown[][] = [];
  let truncated = false;

  for (const r of rows) {
    const userCols = [
      userName(r.user),
      r.user.user_name ?? '',
      r.user.phone ?? '',
      r.user.email ?? '',
      joinNames(r.schools),
      joinNames(r.classes),
      r.activations,
    ];
    if (r.codes.length === 0) {
      if (data.length >= maxRows) {
        truncated = true;
        break;
      }
      data.push([...userCols, '', '', '', '']);
      continue;
    }
    for (const c of r.codes) {
      if (data.length >= maxRows) {
        truncated = true;
        break;
      }
      data.push([...userCols, c.code, targetLabel(c), formatDateTime(c.used_at), statusLabel(c.status)]);
    }
    if (truncated) break;
  }

  return { data, truncated };
}

// Dựng sheet từ dòng đã bung sẵn: tách riêng để nơi gọi biết số dòng có bị cắt không
export function userWorkbookFromRows(
  data: unknown[][],
  f: ReportFilters,
  options?: SummaryOptions
): XLSX.WorkBook {
  const parts = summaryParts(f, options);
  if (f.q) parts.push(`Tìm: "${f.q}"`);

  const ws = buildSheet(
    reportTitle(joinSummary(parts)),
    [
      'Họ tên',
      'Tên đăng nhập',
      'SĐT',
      'Email',
      'Trường',
      'Lớp',
      'Lượt kích hoạt',
      'Mã',
      'Sách/Bộ sách',
      'Ngày dùng',
      'Trạng thái',
    ],
    [28, 18, 14, 28, 35, 20, 14, 20, 40, 18, 14],
    data
  );
  return workbookOf([['Theo người dùng', ws]]);
}
